import {
  Avatar,
  ListItemIcon,
  ListItemText,
  ListItem as MuiListItem,
  ListItemButton as MuiListItemButton,
  styled,
  Tooltip,
} from '@mui/material';
import type {} from '@mui/material/themeCssVarsAugmentation';
import { Features } from '@src/core/features';
import { FC, Fragment, ReactNode } from 'react';

type MenuItem = {
  title: string;
  icon: ReactNode;
  action: Features;
  ignoreDefaultAvatarStyle?: boolean;
};

const MenuItemButton = styled(MuiListItemButton)(({ theme }) => ({
  'borderRadius': 8,
  '&.Mui-selected': {
    '& .MuiListItemIcon-root': {
      color: (theme.vars ?? theme).palette.primary.dark,
    },
    '& .MuiTypography-root': {
      color: (theme.vars ?? theme).palette.text.primary,
    },
    '& .MuiSvgIcon-root': {
      color: (theme.vars ?? theme).palette.primary.dark,
    },
    '& .MuiAvatar-root': {
      backgroundColor: (theme.vars ?? theme).palette.primary.dark,
    },
    '& .MuiTouchRipple-child': {
      backgroundColor: (theme.vars ?? theme).palette.primary.dark,
    },
  },
  '&:not(.ignore) .MuiSvgIcon-root': {
    color: (theme.vars ?? theme).palette.action.active,
  },
  '&:not(.ignore) .MuiAvatar-root': {
    backgroundColor: (theme.vars ?? theme).palette.action.active,
  },
}));

type MenuItemProps = {
  item: Partial<MenuItem>;
  onClick: (() => void) | false;
  isMini?: boolean;
  isSelected?: boolean;
};

type MenuItemStateProps = Required<Pick<MenuItemProps, 'isMini'>> & Pick<MenuItemProps, 'isSelected'>;

const MenuItem: FC<MenuItemProps> = ({ item, isMini = false, isSelected = false, onClick }) => {
  const menuItem = (
    <MuiListItem
      sx={{
        py: 0,
        px: 1,
        overflowX: 'hidden',
      }}
      onClick={() => (typeof onClick === 'function' ? onClick() : undefined)}
    >
      <MenuItemButton
        sx={{
          px: 1.4,
          height: 48,
          ...(onClick === false
            ? {
                'cursor': 'default',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                '&:active': {
                  backgroundColor: 'transparent',
                },
              }
            : {}),
        }}
        selected={isSelected}
        disableTouchRipple={onClick === false}
        className={item?.ignoreDefaultAvatarStyle ? 'ignore' : ''}
      >
        {item.icon || isMini ? (
          <ListItemIcon
            sx={{
              minWidth: 34,
              mr: 1.2,
            }}
          >
            {item.icon ?? null}
            {!item.icon && isMini ? (
              <Avatar
                sx={{
                  width: 34 - 7,
                  height: 34 - 7,
                  fontSize: 12,
                  ml: '-2px',
                }}
              >
                {item.title
                  .split(' ')
                  .slice(0, 2)
                  .map(itemTitleWord => itemTitleWord.charAt(0).toUpperCase())}
              </Avatar>
            ) : null}
          </ListItemIcon>
        ) : null}
        <ListItemText
          primary={item.title}
          sx={{
            whiteSpace: 'nowrap',
            zIndex: 1,
          }}
        />
      </MenuItemButton>
    </MuiListItem>
  );

  return (
    <Fragment>
      {isMini ? (
        <Tooltip title={item.title} placement='right'>
          {menuItem}
        </Tooltip>
      ) : (
        menuItem
      )}
    </Fragment>
  );
};

export { MenuItem };
export type { MenuItemStateProps };
